import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { useCrmSession } from "./CrmSessionAdminContext";
import adminCrmService from "../../services/adminCrmService";
import { createPortal } from "react-dom";
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import authService from "../../services/authService";
import analyticsService from "../../services/analyticsService";
import billingService from "../../services/billingService";
import "../assets/styles/Webadminaddcrm.css";

// CRM icons
import callcrm from "../assets/img/callcrm.svg";
import filterscrm from "../assets/img/filterscrm.svg";
import ladyaddcrm from "../assets/img/ladyaddcrm.svg";
import editcrm from "../assets/img/editcrm.svg";
import deletecrm from "../assets/img/deletecrm.svg"
import close from "../assets/img/close-square.svg";

// Sub-user roles constant (EXACT match with mobile app)
const SUB_USER_ROLES = [
  { key: 'city_manager', label: 'City Manager' },
  { key: 'manager', label: 'Manager' },
  { key: 'sub_user', label: 'Sub User' }
];

export default function Webadminaddcrm() {
  const navigate = useNavigate();
  const { beginSession } = useCrmSession();

  // Core user management state (EXACT match with AdminCrmUsers.js)
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedSubFilter, setSelectedSubFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Date range filter (join date) - matching mobile app
  const [showDateFilterModal, setShowDateFilterModal] = useState(false);
  const [startDate, setStartDate] = useState(''); // YYYY-MM-DD (API format)
  const [endDate, setEndDate] = useState('');     // YYYY-MM-DD (API format)
  // Display YYYY-MM-DD as DD-MM-YYYY
  const toDisplayDate = (ymd) => (ymd ? ymd.split('-').reverse().join('-') : '');
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // Package and credits management
  const [packageData, setPackageData] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);

  // Credits management for builders (EXACT match with mobile app)
  const [creditsAmount, setCreditsAmount] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [creditsBalanceLoading, setCreditsBalanceLoading] = useState(false);

  // Package assignment/extension (EXACT match with mobile app)
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [assignDays, setAssignDays] = useState('');
  const [extendDays, setExtendDays] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [extending, setExtending] = useState(false);
  // Admin-granted free days (subscription.metadata.extended_days) for the expanded user
  const [extendedDays, setExtendedDays] = useState(0);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const plansByRoleRef = useRef({});

  // Sub-user management (EXACT match with mobile app)
  const [subUsers, setSubUsers] = useState([]);
  const [subUserLimits, setSubUserLimits] = useState(null);
  const [loadingSubUsers, setLoadingSubUsers] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('sub_user');
  const [subUserForm, setSubUserForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    role: 'sub_user',
    password: ''
  });
  const [editingSubUser, setEditingSubUser] = useState(null);
  const [showAddSubUser, setShowAddSubUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [packageLoading, setPackageLoading] = useState(false);
  const [extendMonths, setExtendMonths] = useState(1);
  const [credits, setCredits] = useState('');
  const [creditsLoading, setCreditsLoading] = useState(false);

  // Sub-user management
  const [showSubUserModal, setShowSubUserModal] = useState(false);
  const [subUserLoading, setSubUserLoading] = useState(false);

  // Modal states
  const [registerAs, setRegisterAs] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);

  // Add User Form State Variables (EXACT match with mobile app AdminCrmUsers.js)
  const [addUserForm, setAddUserForm] = useState({
    role: 'builder', first_name: '', last_name: '', email: '', phone: '',
    password: '', confirm_password: '',
    company_name: '', representative_name: '', rera_id: '', pan_number: '',
    tan_number: '', gst_number: '', address: '', city: '', state: '', pincode: ''
  });

  // Legacy state variables for backward compatibility
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [panNo, setPanNo] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [reraNo, setReraNo] = useState("");
  const [tanNo, setTanNo] = useState("");
  const [refId, setRefId] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  // OTP and Verification States (EXACT match with mobile app)
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [showEmailOtpField, setShowEmailOtpField] = useState(false);
  const [showPhoneOtpField, setShowPhoneOtpField] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Legacy state for backward compatibility
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [phoneSending, setPhoneSending] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);

  // Dynamic state/city dropdown data (like the mobile app)
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allCitiesData, setAllCitiesData] = useState({});

  // State/City pickers for add user modal (EXACT match with mobile app)
  const [showStatePicker, setShowStatePicker] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);

  // Custom centered alert modal
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // 📅 Custom Date Picker States
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [currentDateType, setCurrentDateType] = useState("");
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  const [selectedYearIdx, setSelectedYearIdx] = useState(0);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0, width: 0 });
  const [filterDates, setFilterDates] = useState({
    fromDate: "",
    toDate: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  function handleDeleteConfirmed() {
    setConfirmOpen(false);
  }

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  const startYear = 2015;
  const endYear = new Date().getFullYear() + 20;
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => (startYear + i).toString()
  );

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const ITEM_HEIGHT = 40;
  let scrollTimeout = null;

  // Load state and city data from backend (like mobile app)
  const loadStateCityData = async () => {
    try {
      const citiesStatesResponse = await analyticsService.getCitiesAndStates();

      if (citiesStatesResponse.success && citiesStatesResponse.data) {
        const { states, cities } = citiesStatesResponse.data;
        setStateOptions(states || []);
        setAllCitiesData(cities || {});
        console.log('✅ Loaded states and cities for CRM:', {
          states: states?.length,
          cities: Object.keys(cities || {}).length
        });
      } else {
        console.warn('⚠️ Failed to load state/city data');
      }
    } catch (error) {
      console.error('Error loading state/city data:', error);
    }
  };

  // Load data when component mounts (from AdminCrmUsers.js)
  useEffect(() => {
    console.log('🚀 Component mounted, loading initial data...');
    loadStateCityData();

    // Load users immediately
    const initialLoadUsers = async () => {
      console.log('📍 Initial user load starting...');
      try {
        setLoading(true);
        const response = await authService.getUsers({ page: 1, limit: 50 });
        console.log('📥 Initial users response:', response);

        if (response.success && response.data) {
          const newUsers = response.data.users || response.data || [];
          console.log('👥 Initial users loaded:', newUsers.length);
          setUsers(newUsers);
          setPage(2);
          setHasMore(newUsers.length === 50);
        } else {
          console.warn('❌ Initial load failed:', response.message);
          // Set dummy data for testing
          setUsers([
            {
              id: 1,
              full_name: 'Demo Agent',
              email: 'agent@demo.com',
              phone: '9876543210',
              role: 'agent',
              subscription_status: 'active',
              created_at: new Date().toISOString(),
              state: 'Maharashtra',
              city: 'Mumbai'
            },
            {
              id: 2,
              full_name: 'Demo Builder',
              email: 'builder@demo.com',
              phone: '9876543211',
              role: 'builder',
              subscription_status: 'free',
              created_at: new Date().toISOString(),
              state: 'Karnataka',
              city: 'Bangalore',
              credits: 1500
            },
            {
              id: 3,
              full_name: 'Demo Agency',
              email: 'agency@demo.com',
              phone: '9876543212',
              role: 'agency',
              subscription_status: 'active',
              created_at: new Date().toISOString(),
              state: 'Tamil Nadu',
              city: 'Chennai'
            }
          ]);
        }
      } catch (error) {
        console.error('💥 Initial load error:', error);
        // Set dummy data on error
        setUsers([
          {
            id: 1,
            full_name: 'Demo User',
            email: 'demo@rebax.in',
            phone: '9876543210',
            role: 'agent',
            subscription_status: 'active',
            created_at: new Date().toISOString(),
            state: 'Maharashtra',
            city: 'Mumbai'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    initialLoadUsers();
    loadPackageData();
  }, []);

  // EXACT useEffect patterns from AdminCrmUsers.js
  const searchTimeoutRef = useRef(null);

  // Backend's subscription_status filter misses users with no subscription record
  // (shown as "Free" in the UI), so fetch everyone once and classify client-side.
  const PAGE_LIMIT = 500;

  const fetchUsers = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = { page: pageNum, limit: PAGE_LIMIT };
      if (searchText.trim()) params.search = searchText.trim();
      if (selectedRole !== 'all') params.role = selectedRole;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      console.log('🔍 Fetching users with params:', params);

      const res = await authService.getUsers(params);
      console.log('📥 Users API response:', res);

      if (res.success) {
        const newUsers = res.data?.users || res.data || [];
        if (append) {
          setUsers(prev => [...prev, ...newUsers]);
        } else {
          setUsers(newUsers);
        }
        setHasMore(newUsers.length === PAGE_LIMIT);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchText, selectedRole, startDate, endDate]);

  useEffect(() => {
    fetchUsers(1);
  }, [selectedRole, startDate, endDate]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(1);
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchText]);

  // Add loadMore function exactly like mobile app
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchUsers(page + 1, true);
    }
  };

  // Package assignment handlers (like mobile app)
  const handleAssignPackage = async (user) => {
    const days = parseInt(extendMonths);
    if (!selectedPackageId || !days || days < 1) {
      toast.warning('Please select a plan and number of days');
      return;
    }
    try {
      setPackageLoading(true);
      const res = await adminCrmService.assignPlan(user.id, selectedPackageId, days);
      if (res.success) {
        toast.success(`Plan assigned for ${days} day(s). Discount value: ₹${res.data?.discount_value || 0}`);
        setSelectedPackageId('');
        setExtendMonths(1);
        fetchUsers(1);
      } else {
        toast.error(res.message || 'Failed to assign package');
      }
    } catch (error) {
      console.error('Package assignment error:', error);
      toast.error('Failed to assign package');
    } finally {
      setPackageLoading(false);
    }
  };

  const handleExtendPackage = async (user) => {
    const days = parseInt(extendMonths);
    if (!days || days < 1) {
      toast.warning('Please enter number of days');
      return;
    }
    try {
      setPackageLoading(true);
      const res = await adminCrmService.extendSubscription(user.id, days);
      if (res.success) {
        toast.success(`Subscription extended by ${days} day(s). Discount value: ₹${res.data?.discount_value || 0}`);
        setExtendMonths(1);
        fetchUsers(1);
      } else {
        toast.error(res.message || 'Failed to extend package');
      }
    } catch (error) {
      console.error('Package extension error:', error);
      toast.error('Failed to extend package');
    } finally {
      setPackageLoading(false);
    }
  };

  // Load package data (matching mobile app exactly)
  const loadPackageData = async (role = 'agent') => {
    try {
      setPackageLoading(true);
      const response = await billingService.getPublicSubscriptionPlansWithFilters({
        userType: role,
        period: 'monthly'
      });

      if (response?.success && response?.data?.plans) {
        // Filter for paid subscription packages like in mobile app
        const paidPlans = response.data.plans.filter(p =>
          parseFloat(p.offer_price) > 0 &&
          p.package_type === 'subscription_package'
        );
        setPackageData(paidPlans);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setPackageLoading(false);
    }
  };

  // Search handler
  const handleSearch = useCallback((text) => {
    setSearchText(text);
  }, []);

  // Mirrors mobile app: latest active subscription_package row; admin-assigned/₹0 plans
  // count every remaining day as free, otherwise free days = metadata.extended_days.
  const fetchExtendedDays = async (userId) => {
    try {
      const res = await adminCrmService.getUserMembership(userId);
      const history = res?.data?.subscription_history || res?.data?.data?.subscription_history || [];
      const sub = history.find(s =>
        s.package_type === 'subscription_package' && ['active', 'trial', 'pending'].includes(s.status)
      );
      if (!sub) return;
      const isAdminAssigned = sub.metadata?.admin_assigned === true;
      const isZeroPaid = Number(sub.amount_paid) === 0 || Number(sub.offer_price) === 0;
      if (isAdminAssigned || isZeroPaid) {
        const end = new Date(sub.current_period_end);
        setExtendedDays(Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24))) || 0);
      } else {
        setExtendedDays(parseInt(sub.metadata?.extended_days, 10) || 0);
      }
    } catch (err) {
      console.error('Fetch extended days error:', err);
    }
  };

  // current_period_end already includes admin-extended days, so split them out.
  // Free days are clamped so a reduction larger than the free days spills into subscription days.
  const getPackageDaysSplit = (user) => {
    const end = user?.subscription?.current_period_end;
    const daysRemaining = end
      ? Math.max(0, Math.ceil((new Date(end) - new Date()) / (1000 * 60 * 60 * 24)))
      : 0;
    const freeDays = Math.min(daysRemaining, Math.max(0, extendedDays));
    return { daysRemaining, freeDays, subscriptionDays: daysRemaining - freeDays };
  };

  // Toggle card expansion (EXACT match with mobile app behavior)
  const toggleCardExpansion = useCallback((userId) => {
    setExpandedId(prev => {
      const isExpanding = prev !== userId;
      if (isExpanding) {
        const user = users.find(u => u.id === userId);
        if (user) {
          // Load plans for free users (like mobile app)
          if (isFreeUser(user) && user.role) {
            loadPlansForRole(user.role);
          }
          // Fetch sub-users automatically for agencies/builders (like mobile app)
          if (user.role?.toLowerCase() === 'agency' || user.role?.toLowerCase() === 'builder') {
            fetchSubUsers(userId);
          }
          // Fetch credit balance for builders (like mobile app)
          setCreditsAmount('');
          setCreditsBalance(null);
          if (user.role?.toLowerCase() === 'builder') {
            fetchBuilderCredits(userId);
          }
          setExtendDays('');
          setExtendedDays(0);
          if (!isFreeUser(user)) {
            fetchExtendedDays(userId);
          }
        }
      }
      return prev === userId ? null : userId;
    });
  }, [users]);

  // Handle package assignment (for modal)
  const handleAssignPackageModal = async () => {
    if (!selectedUser || !selectedPackageId) {
      toast.warning('Please select a package');
      return;
    }

    try {
      setPackageLoading(true);

      const days = parseInt(extendMonths);
      const res = await adminCrmService.assignPlan(selectedUser.id, selectedPackageId, days);
      if (res.success) {
        toast.success(`Plan assigned for ${days} day(s). Discount value: ₹${res.data?.discount_value || 0}`);
        setShowPackageModal(false);
        fetchUsers(1);
      } else {
        toast.error(res.message || 'Failed to assign package');
      }
    } catch (error) {
      console.error('Error assigning package:', error);
      toast.error('Failed to assign package');
    } finally {
      setPackageLoading(false);
    }
  };

  // Handle add credits (EXACT match with mobile app)
  const handleAddCredits = async (userId) => {
    if (!creditsAmount || parseInt(creditsAmount) <= 0) {
      toast.warning('Please enter a valid credit amount');
      return;
    }

    try {
      setAddingCredits(true);

      // Use the same API as mobile app
      const response = await authService.assignCredits({
        user_id: userId,
        credits: parseInt(creditsAmount),
        description: 'Admin CRM assignment'
      });

      if (response.success) {
        toast.success(`${creditsAmount} credits assigned successfully!`);
        setCreditsAmount('');
        fetchBuilderCredits(userId);
        // Refresh user data
        await fetchUsers(1, false);
      } else {
        toast.error(response.message || 'Failed to assign credits');
      }
    } catch (error) {
      console.error('Add credits error:', error);
      toast.error('Failed to assign credits');
    } finally {
      setAddingCredits(false);
    }
  };

  // Fetch builder credit balance (EXACT match with mobile app)
  const fetchBuilderCredits = async (userId) => {
    setCreditsBalanceLoading(true);
    try {
      const res = await authService.getBuilderBalance(userId);
      if (res?.success && res?.data) {
        setCreditsBalance(res.data.total_credits ?? res.data.balance ?? 0);
      } else {
        setCreditsBalance(0);
      }
    } catch (error) {
      console.error('Fetch builder credits error:', error);
      setCreditsBalance(0);
    } finally {
      setCreditsBalanceLoading(false);
    }
  };

  // Fetch sub-users (EXACT match with mobile app)
  const fetchSubUsers = async (userId) => {
    try {
      setLoadingSubUsers(true);

      const [usersRes, limitsRes] = await Promise.all([
        authService.getSubUsers(userId),
        authService.getSubUserLimits(userId)
      ]);

      if (usersRes.success) {
        setSubUsers(usersRes.data || []);
      } else {
        console.error('Failed to fetch sub-users:', usersRes.message);
        setSubUsers([]);
      }

      if (limitsRes.success) {
        setSubUserLimits(limitsRes.data || null);
      } else {
        setSubUserLimits(null);
      }
    } catch (error) {
      console.error('Fetch sub-users error:', error);
      setSubUsers([]);
      setSubUserLimits(null);
    } finally {
      setLoadingSubUsers(false);
    }
  };

  // Handle add sub-user (EXACT match with mobile app)
  const handleAddSubUser = async (parentId) => {
    if (!subUserForm.full_name.trim()) {
      toast.warning('Please enter full name');
      return;
    }

    if (!subUserForm.phone.trim()) {
      toast.warning('Please enter phone number');
      return;
    }

    try {
      const response = await authService.addSubUser({
        parent_id: parentId,
        ...subUserForm,
        password: subUserForm.password || 'Rebax@123'
      });

      if (response.success) {
        toast.success('Sub-user created successfully!');
        setShowAddSubUser(false);
        setSubUserForm({ full_name: '', email: '', phone: '', city: '', role: 'sub_user', password: '' });
        fetchSubUsers(parentId);
      } else {
        toast.error(response.message || 'Failed to create sub-user');
      }
    } catch (error) {
      console.error('Add sub-user error:', error);
      toast.error('Failed to create sub-user');
    }
  };

  // Handle delete sub-user (EXACT match with mobile app)
  const handleDeleteSubUser = async (parentId, subUserId, subUserName) => {
    try {
      const response = await authService.deleteSubUser({
        parent_id: parentId,
        sub_user_id: subUserId
      });

      if (response.success) {
        toast.success('Sub-user deleted successfully!');
        fetchSubUsers(parentId);
      } else {
        toast.error(response.message || 'Failed to delete sub-user');
      }
    } catch (error) {
      console.error('Delete sub-user error:', error);
      toast.error('Failed to delete sub-user');
    }
  };

  // Function to calculate days in month with leap year
  const getDaysInMonth = (monthIdx, year) => {
    return new Date(year, monthIdx + 1, 0).getDate();
  };

  // Memoized days array based on selected month + year
  const days = useMemo(() => {
    const y = parseInt(years[selectedYearIdx] || new Date().getFullYear());
    const m = selectedMonthIdx >= 0 ? selectedMonthIdx : new Date().getMonth();
    const totalDays = getDaysInMonth(m, y);

    if (selectedDayIdx >= totalDays) {
      setSelectedDayIdx(totalDays - 1);
    }

    return Array.from({ length: totalDays }, (_, i) =>
      (i + 1).toString().padStart(2, "0")
    );
  }, [selectedMonthIdx, selectedYearIdx, years, selectedDayIdx]);

  // Open date picker function
  const openDatePicker = (type, e) => {
    setCurrentDateType(type);

    const rect = e.currentTarget.getBoundingClientRect();
    setPickerPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    });

    const savedDate = filterDates[type];
    let dayIdx, monthIdx, yearIdx;

    if (savedDate) {
      const [d, m, y] = savedDate.split(" ");
      dayIdx = parseInt(d, 10) - 1;
      monthIdx = months.indexOf(m);
      yearIdx = years.indexOf(y);
    } else {
      const today = new Date();
      dayIdx = today.getDate() - 1;
      monthIdx = today.getMonth();
      yearIdx = years.indexOf(today.getFullYear().toString());
    }

    setSelectedDayIdx(dayIdx >= 0 ? dayIdx : 0);
    setSelectedMonthIdx(monthIdx >= 0 ? monthIdx : 0);
    setSelectedYearIdx(yearIdx >= 0 ? yearIdx : 0);

    setIsDatePickerVisible(true);
  };

  // Scroll to selected index
  useEffect(() => {
    if (!isDatePickerVisible) return;

    const scrollToIndex = (ref, index) => {
      if (!ref.current) return;
      ref.current.scrollTop = index * ITEM_HEIGHT;
    };

    requestAnimationFrame(() => {
      scrollToIndex(dayRef, selectedDayIdx);
      scrollToIndex(monthRef, selectedMonthIdx);
      scrollToIndex(yearRef, selectedYearIdx);
    });
  }, [isDatePickerVisible, selectedDayIdx, selectedMonthIdx, selectedYearIdx]);

  // Save date function
  const handleSaveDate = () => {
    const day = days[selectedDayIdx] ?? days[0];
    const month = months[selectedMonthIdx] ?? months[0];
    const year = years[selectedYearIdx] ?? years[0];
    const picked = `${day} ${month} ${year}`;
    setFilterDates((prev) => ({ ...prev, [currentDateType]: picked }));
    setIsDatePickerVisible(false);
  };

  // Handle scroll function
  const handleScroll = (ref, setIndex, itemCount) => {
    if (!ref.current) return;
    const { scrollTop } = ref.current;
    const index = Math.round(scrollTop / ITEM_HEIGHT);

    if (index >= 0 && index < itemCount) {
      setIndex(index);
    }

    if (scrollTimeout) clearTimeout(scrollTimeout);

    scrollTimeout = setTimeout(() => {
      if (!ref.current) return;
      const snapIndex = Math.round(ref.current.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(snapIndex, itemCount - 1));

      ref.current.scrollTo({
        top: clamped * ITEM_HEIGHT,
        behavior: "smooth",
      });

      setIndex(clamped);
    }, 150);
  };

  // Add User Form Functions (copied from AdminCrm.js)

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate registration form
  const validateRegistrationForm = () => {
    // Check Register As (mandatory)
    if (!registerAs) {
      toast.warning('Please select "Register As" option');
      return false;
    }

    // Check full name
    if (!fullName || fullName.trim().length < 2) {
      toast.warning('Please enter a valid full name');
      return false;
    }

    // Check email
    if (!email || !validateEmail(email)) {
      toast.warning('Please enter a valid email address');
      return false;
    }

    // Check if email is verified
    if (!isEmailVerified) {
      toast.warning('Please verify your email address before continuing');
      return false;
    }

    // Check phone
    if (!phone || phone.trim().length < 10) {
      toast.warning('Please enter a valid phone number');
      return false;
    }

    // Check if phone is verified
    if (!isPhoneVerified) {
      toast.warning('Please verify your phone number before continuing');
      return false;
    }

    // Check password
    if (!password || password.length < 6) {
      toast.warning('Password must be at least 6 characters long');
      return false;
    }

    // Check confirm password
    if (!confirmPassword || password !== confirmPassword) {
      toast.warning('Passwords do not match');
      return false;
    }

    return true;
  };

  // Send Email OTP (EXACT match with mobile app)
  const handleSendEmailOtp = async () => {
    if (!addUserForm.email?.trim()) {
      showCenteredAlert('Enter email first');
      return;
    }
    setSendingEmailOtp(true);
    try {
      const res = await authService.sendOTP(addUserForm.email.trim(), 'email');
      if (res.success) {
        setShowEmailOtpField(true);
        showCenteredAlert('OTP sent to email');
      } else {
        showCenteredAlert(res.message || 'Failed to send OTP');
      }
    } catch (err) {
      showCenteredAlert('Failed to send email OTP');
    } finally {
      setSendingEmailOtp(false);
    }
  };

  // Send Phone OTP (EXACT match with mobile app)
  const handleSendPhoneOtp = async () => {
    if (!addUserForm.phone?.trim() || addUserForm.phone.trim().length < 10) {
      showCenteredAlert('Enter valid phone number');
      return;
    }
    setSendingPhoneOtp(true);
    try {
      const res = await authService.sendOTP(addUserForm.phone.trim(), 'phone');
      if (res.success) {
        setShowPhoneOtpField(true);
        showCenteredAlert('OTP sent to phone');
      } else {
        showCenteredAlert(res.message || 'Failed to send OTP');
      }
    } catch (err) {
      showCenteredAlert('Failed to send phone OTP');
    } finally {
      setSendingPhoneOtp(false);
    }
  };

  // Verify Email OTP (EXACT match with mobile app)
  const handleVerifyEmailOtp = async () => {
    if (!emailOtp.trim()) {
      showCenteredAlert('Enter OTP');
      return;
    }
    setVerifyingEmail(true);
    try {
      const res = await authService.verifyOTP(addUserForm.email.trim(), emailOtp.trim(), 'email');
      if (res.success) {
        setEmailVerified(true);
        setShowEmailOtpField(false);
        showCenteredAlert('Email verified');
      } else {
        showCenteredAlert(res.message || 'Invalid OTP');
      }
    } catch (err) {
      showCenteredAlert('OTP verification failed');
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Verify Phone OTP (EXACT match with mobile app)
  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtp.trim()) {
      showCenteredAlert('Enter OTP');
      return;
    }
    setVerifyingPhone(true);
    try {
      const res = await authService.verifyOTP(addUserForm.phone.trim(), phoneOtp.trim(), 'phone');
      if (res.success) {
        setPhoneVerified(true);
        setShowPhoneOtpField(false);
        showCenteredAlert('Phone verified');
      } else {
        showCenteredAlert(res.message || 'Invalid OTP');
      }
    } catch (err) {
      showCenteredAlert('OTP verification failed');
    } finally {
      setVerifyingPhone(false);
    }
  };

  // Reset add user form (EXACT match with mobile app)
  const resetAddUserForm = () => {
    setAddUserForm({
      role: 'builder', first_name: '', last_name: '', email: '', phone: '',
      password: '', confirm_password: '',
      company_name: '', representative_name: '', rera_id: '', pan_number: '',
      tan_number: '', gst_number: '', address: '', city: '', state: '', pincode: ''
    });
    setEmailOtp('');
    setPhoneOtp('');
    setEmailVerified(false);
    setPhoneVerified(false);
    setShowEmailOtpField(false);
    setShowPhoneOtpField(false);
    setShowPassword(false);
    setShowConfirmPassword(false);

    // Reset legacy variables too
    setRegisterAs('');
    setFullName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setPanNo('');
    setGstNo('');
    setReraNo('');
    setTanNo('');
    setRefId('');
    setAddress('');
    setCity('');
    setState('');
    setPincode('');
    setEmailOtpSent(false);
    setPhoneOtpSent(false);
    setIsEmailVerified(false);
    setIsPhoneVerified(false);
    setEmailSending(false);
    setPhoneSending(false);
    setCityOptions([]);
    setShowAddUserModal(false);
  };

  // Simple register user function (matching mobile app UI)
  const handleSimpleRegisterUser = async () => {
    const { role, email, phone, first_name, last_name, company_name, password } = addUserForm;

    if (!first_name?.trim()) {
      showCenteredAlert('Full name is required');
      return;
    }
    if (!email?.trim()) {
      showCenteredAlert('Email is required');
      return;
    }
    if (!phone?.trim()) {
      showCenteredAlert('Phone is required');
      return;
    }
    if (!role) {
      showCenteredAlert('Please select a role');
      return;
    }

    setAddingUser(true);
    try {
      const payload = {
        role: role,
        email: email.trim(),
        phone: phone.trim(),
        full_name: role === 'agent'
          ? `${first_name} ${last_name}`.trim()
          : (company_name || `${first_name} ${last_name}`.trim()),
        first_name: first_name.trim(),
        last_name: last_name?.trim() || '',
        company_name: company_name?.trim() || '',
        city: addUserForm.city?.trim() || '',
        state: addUserForm.state?.trim() || '',
        password: password?.trim() || 'Rebax@123', // Use form password or default
      };

      const res = await authService.registerCrmUser(payload);
      if (res.success) {
        showCenteredAlert('User registered successfully');
        resetAddUserForm();
        fetchUsers(1);
      } else {
        showCenteredAlert(res.message || 'Registration failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to register user';
      showCenteredAlert(msg);
    } finally {
      setAddingUser(false);
    }
  };

  // Handle user registration (EXACT match with mobile app)
  const handleRegisterUser = async () => {
    const { role, email, phone } = addUserForm;
    if (!role) {
      showCenteredAlert('Select user type');
      return;
    }
    if (!email?.trim()) {
      showCenteredAlert('Email is required');
      return;
    }
    if (!phone?.trim()) {
      showCenteredAlert('Phone is required');
      return;
    }
    if (!emailVerified) {
      showCenteredAlert('Please verify email OTP first');
      return;
    }
    if (!phoneVerified) {
      showCenteredAlert('Please verify phone OTP first');
      return;
    }
    if (!addUserForm.password?.trim()) {
      showCenteredAlert('Password is required');
      return;
    }
    if (addUserForm.password.length < 8) {
      showCenteredAlert('Password must be at least 8 characters');
      return;
    }
    if (addUserForm.password !== addUserForm.confirm_password) {
      showCenteredAlert('Passwords do not match');
      return;
    }
    if (role === 'agent' && !addUserForm.first_name?.trim()) {
      showCenteredAlert('First name is required');
      return;
    }
    if ((role === 'agency' || role === 'builder') && !addUserForm.company_name?.trim()) {
      showCenteredAlert('Company name is required');
      return;
    }

    setAddingUser(true);
    try {
      const { confirm_password, ...formData } = addUserForm;
      const payload = {
        ...formData,
        full_name: role === 'agent'
          ? `${addUserForm.first_name || ''} ${addUserForm.last_name || ''}`.trim()
          : addUserForm.company_name,
      };
      const res = await authService.registerCrmUser(payload);
      if (res.success) {
        showCenteredAlert('User registered successfully');
        resetAddUserForm();
        fetchUsers(1);
      } else {
        showCenteredAlert(res.message || 'Failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to register user';
      showCenteredAlert(msg);
    } finally {
      setAddingUser(false);
    }
  };

  // Indian States and Cities Data (EXACT match with mobile app)
  const STATES_LIST = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh', 'Puducherry'
  ];

  const getCitiesByState = (state) => {
    const citiesMap = {
      'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur'],
      'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum', 'Gulbarga', 'Shimoga'],
      'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
      'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar'],
      'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Bikaner', 'Ajmer'],
      'Delhi': ['New Delhi', 'Central Delhi', 'North Delhi', 'South Delhi', 'East Delhi', 'West Delhi'],
      'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri'],
      'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut', 'Allahabad', 'Bareilly'],
      'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak'],
      'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
      'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam'],
      'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool']
    };
    return citiesMap[state] || [];
  };

  // Custom centered alert function
  const showCenteredAlert = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlertMessage('');
  };

  // Old reset form function removed - using new one that matches mobile app

  // Handle state change and load corresponding cities (like mobile app)
  const handleStateChange = (selectedState) => {
    setState(selectedState);

    if (selectedState) {
      const cities = allCitiesData[selectedState] || [];
      setCityOptions(cities);
      setCity(''); // Reset city selection when state changes
      console.log(`✅ Selected state: ${selectedState}, cities available: ${cities.length}`);
    } else {
      setCityOptions([]);
      setCity('');
    }
  };

  // Helper functions (from AdminCrmUsers.js)
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'subscribed':
        return '#22C55E'; // green
      case 'expired':
      case 'unsubscribed':
        return '#EF4444'; // red
      case 'free':
      case 'trial':
        return '#3B82F6'; // blue
      default:
        return '#6B7280'; // gray
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getSubscriptionStatus = (user) => {
    // API returns subscription.status, not subscription_status
    const subscriptionStatus = user.subscription?.status;
    if (!subscriptionStatus) return 'free';

    const status = subscriptionStatus.toLowerCase();
    if (status === 'subscribed') return 'subscribed';
    if (status === 'expired') return 'expired';
    if (status === 'cancelled' || status === 'unsubscribed') return 'unsubscribed';
    if (status === 'active') return 'subscribed';
    return 'free';
  };

  // Check if user is free — matching mobile app AdminCrmUsers.js exactly
  const isFreeUser = (user) => {
    if (!user.subscription) return true;
    const status = user.subscription.status?.toLowerCase();
    return !status || status === 'free' || status === 'none';
  };

  // Get plans for specific role (EXACT match with mobile app)
  const getRolePlans = (role) => {
    return plansByRoleRef.current[role?.toLowerCase()] || [];
  };

  // Get cost preview for assignment (EXACT match with mobile app)
  const getAssignCostPreview = () => {
    if (!selectedPlan || !assignDays || parseInt(assignDays) <= 0) return null;
    if (!selectedPlan.offer_price || !selectedPlan.duration_days) return null;
    const days = parseInt(assignDays);
    const costPerDay = selectedPlan.offer_price / selectedPlan.duration_days;
    return Math.round(costPerDay * days);
  };

  // Load plans for role (matching mobile app fetchRolePlans exactly)
  const loadPlansForRole = async (role) => {
    const key = role?.toLowerCase();
    if (!key) return;
    if (plansByRoleRef.current[key]) return; // already cached
    try {
      setPlansLoading(true);
      const response = await billingService.getPublicSubscriptionPlansWithFilters({ userType: key, period: 'monthly' });
      // billingService wraps apiGet: response.data = { success, data: rawApiData }
      // rawApiData may be { plans: [...] } or array directly
      const payload = response?.data?.data || response?.data;
      const plansArray = payload?.plans || (Array.isArray(payload) ? payload : []);
      const paidPlans = plansArray.filter(p =>
        parseFloat(p.offer_price) > 0 &&
        p.package_type === 'subscription_package'
      );
      plansByRoleRef.current[key] = paidPlans;
      setPlans(prev => [...prev]); // trigger re-render so getRolePlans returns updated data
    } catch (error) {
      console.error('Load plans error:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle assign plan (EXACT match with mobile app)
  const handleAssignPlan = async (userId) => {
    if (!selectedPlan) {
      toast.warning('Please select a plan');
      return;
    }
    const days = parseInt(assignDays);
    if (!days || days < 1) {
      toast.warning('Please enter valid number of days');
      return;
    }
    if (assigning) return;
    try {
      setAssigning(true);
      const res = await adminCrmService.assignPlan(userId, selectedPlan.id, days);
      if (res.success) {
        toast.success(`Plan assigned for ${days} day(s). Discount value: ₹${res.data?.discount_value || 0}`);
        setSelectedPlan(null);
        setAssignDays('');
        await fetchUsers(1, false);
      } else {
        toast.error(res.message || 'Failed to assign plan');
      }
    } catch (error) {
      console.error('Assign plan error:', error);
      toast.error(error?.message || 'Failed to assign plan');
    } finally {
      setAssigning(false);
    }
  };

  // Handle extend subscription (matching mobile app exactly)
  const handleExtend = async (userId) => {
    const days = parseInt(extendDays);
    // Negative days reduce the package duration
    if (!days) {
      toast.warning('Please enter valid number of days');
      return;
    }
    if (extending) return;

    if (days < 0) {
      // Reduction: deduct from free days first, then from subscription days
      const user = users.find(u => u.id === userId);
      const { daysRemaining, freeDays, subscriptionDays } = getPackageDaysSplit(user);
      const reduceBy = Math.abs(days);
      if (reduceBy > daysRemaining) {
        toast.error(`Cannot reduce more than ${daysRemaining} remaining day(s)`);
        return;
      }
      const fromFree = Math.min(reduceBy, freeDays);
      const fromSubscription = reduceBy - fromFree;
      const confirmed = window.confirm(
        `Reduce package by ${reduceBy} day(s)?\n\n` +
        `Free days: ${freeDays} → ${freeDays - fromFree} (-${fromFree})\n` +
        `Subscription days: ${subscriptionDays} → ${subscriptionDays - fromSubscription} (-${fromSubscription})`
      );
      if (!confirmed) return;
    }

    try {
      setExtending(true);
      const res = await adminCrmService.extendSubscription(userId, days);
      if (res.success) {
        toast.success(`Subscription ${days < 0 ? 'reduced' : 'extended'} by ${Math.abs(days)} day(s). Discount value: ₹${res.data?.discount_value || 0}`);
        setExtendDays('');
        await fetchUsers(1, false);
        fetchExtendedDays(userId);
      } else {
        toast.error(res.message || 'Failed to extend subscription');
      }
    } catch (error) {
      console.error('Extend subscription error:', error);
      toast.error(error?.message || 'Failed to extend subscription');
    } finally {
      setExtending(false);
    }
  };

  // Matches mobile app: no subscription record, or an unrecognized/absent status,
  // both count as "Free".
  const getUserSubStatusKey = (user) => {
    const status = user.subscription?.status?.toLowerCase();
    return status === 'subscribed' || status === 'unsubscribed' ? status : 'free';
  };

  // Filter users based on selected filters, and count each subscription filter
  const { filteredUsers, subFilterCounts } = useMemo(() => {
    const counts = { subscribed: 0, free: 0, unsubscribed: 0 };
    const filtered = users.filter(user => {
      const roleMatch = selectedRole === 'all' || user.role?.toLowerCase() === selectedRole;
      if (!roleMatch) return false;
      // Join-date range (also sent to the API, but the backend doesn't filter on it yet)
      const joined = user.created_at ? new Date(user.created_at).toLocaleDateString('en-CA') : '';
      if (startDate && (!joined || joined < startDate)) return false;
      if (endDate && (!joined || joined > endDate)) return false;
      const key = getUserSubStatusKey(user);
      counts[key]++;
      return selectedSubFilter === 'all' || key === selectedSubFilter;
    });
    return { filteredUsers: filtered, subFilterCounts: counts };
  }, [users, selectedRole, selectedSubFilter, startDate, endDate]);

  // Remove old unused functions - using toggleCardExpansion instead

  const handleClick = (user) => {
    const userRole = user.role?.toLowerCase();
    const userId = user.id;

    if (userRole === 'agency') {
      navigate(`/AdminAgencyProfile`, {
        state: {
          user,
          userId: userId,
          partnerData: {
            partner_id: userId,
            name: user.full_name || user.name,
            company_name: user.company_name || user.full_name || user.name,
            phone: user.phone,
            email: user.email,
            avatar_url: user.avatar_url,
            banner_image: user.banner_image,
            city: user.city,
            state: user.state,
            experience: user.experience,
            about: user.about,
            status: user.status,
            role: userRole,
            rera_id: user.rera_id,
          }
        }
      });
    } else if (userRole === 'agent') {
      navigate(`/AdminAgentProfile`, {
        state: {
          user,
          userId: userId,
          partnerData: {
            partner_id: userId,
            name: user.full_name || user.name,
            company_name: user.company_name || user.full_name || user.name,
            phone: user.phone,
            email: user.email,
            avatar_url: user.avatar_url,
            banner_image: user.banner_image,
            city: user.city,
            state: user.state,
            experience: user.experience,
            about: user.about,
            status: user.status,
            role: userRole,
            rera_id: user.rera_id,
          }
        }
      });
    } else if (userRole === 'builder') {
      navigate(`/AdminAgencyViewBuilder`, {
        state: {
          user,
          userId: userId,
          partnerData: {
            partner_id: userId,
            name: user.full_name || user.name,
            company_name: user.company_name || user.full_name || user.name,
            phone: user.phone,
            email: user.email,
            avatar_url: user.avatar_url,
            banner_image: user.banner_image,
            city: user.city,
            state: user.state,
            experience: user.experience,
            about: user.about,
            status: user.status,
            role: userRole,
            rera_id: user.rera_id,
          }
        }
      });
    } else {
      console.warn("Unknown user role:", userRole);
      // Default fallback to agent profile for unknown roles
      navigate(`/AdminAgentProfile`, {
        state: {
          user,
          userId: userId,
          partnerData: {
            partner_id: userId,
            name: user.full_name || user.name,
            company_name: user.company_name || user.full_name || user.name,
            phone: user.phone,
            email: user.email,
            avatar_url: user.avatar_url,
            banner_image: user.banner_image,
            city: user.city,
            state: user.state,
            role: userRole || 'agent',
          }
        }
      });
    }
  };

  const handleCrmAccessClick = (user) => {
    setSelectedUser(user);
    setPassword("");
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (password.trim() === "") {
      toast.warning("Please enter your password");
      return;
    }
    try {
      const pwRes = await adminCrmService.verifyPassword(password);
      if (!pwRes?.success) {
        toast.error(pwRes?.message || 'Incorrect password. Please try again.');
        return;
      }
      const otpRes = await adminCrmService.sendOtp(selectedUser.id);
      if (!otpRes?.success) {
        toast.error(otpRes?.message || 'Failed to send OTP. Please try again.');
        return;
      }
      setShowPasswordModal(false);
      setOtp(["", "", "", ""]);
      setShowOtpModal(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleOtpSubmit = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 4) {
      toast.warning("Please enter a valid 4-digit OTP");
      return;
    }
    // Open blank window BEFORE the async call so browser doesn't block it as a popup
    const portalWindow = window.open('about:blank', '_blank');
    try {
      const res = await adminCrmService.startSession(selectedUser.id, otpValue);
      if (!res?.success) {
        portalWindow?.close();
        toast.error(res?.message || 'Failed to start CRM session. Please try again.');
        return;
      }
      const { token, user } = res.data || {};
      const portalUrl = `https://portal.rebax.in/crm-session?token=${token}&role=${user?.role}&userId=${user?.id}`;
      portalWindow.location.href = portalUrl;
      beginSession(user?.id || selectedUser.id, portalWindow);
      setShowOtpModal(false);
      setOtp(["", "", "", ""]);
    } catch (err) {
      portalWindow?.close();
      toast.error('Failed to start CRM session. Please try again.');
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    setPassword("");
    setOtp(["", "", "", ""]);
  };

  return (
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div style={{ border: "1px solid #E2E4E9", borderRadius: "10px" }} className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="crm-addpage-main-title">User Management</div>
              <div className="d-flex align-items-center gap-3">
                <div className="crm-addpage-total-users">Total Users : {filteredUsers.length}</div>
                <button
                  className="crm-addpage-filter-button d-flex align-items-center gap-2"
                  onClick={() => setShowDateFilterModal(true)}
                  style={{ position: 'relative' }}
                >
                  <img src={filterscrm} alt="Filter Icon" width="20" height="20" />
                  <span>Filters</span>
                  {(startDate || endDate) && (
                    <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: 4, backgroundColor: '#059669' }} />
                  )}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="search-container">
                {/* Own class: the global .search-input (TopNav.css) strips border/background with !important */}
                <input
                  type="text"
                  className="form-control crm-user-search-input"
                  placeholder="Search users by name, email, or phone..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Total Count Row (like mobile app) */}
            {/* <div className="mb-3 p-2" style={{ backgroundColor: '#fff', borderBottom: '1px solid #EEEEF2' }}>
              <span style={{ fontSize: '13px', color: '#6B7280' }}>
                Total Users: <span style={{ fontWeight: '700', color: '#1a1a2e' }}>{totalCount}</span>
              </span>
            </div> */}

            {/* Role + Sub Filters (always visible, like mobile app) */}
            {(
              <div className="mb-4" style={{ backgroundColor: '#fff', padding: '10px 0', borderBottom: '1px solid #EEEEF2' }}>
                <div className="d-flex gap-2 flex-wrap px-3">
                  {/* Role filters */}
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'agent', label: 'Agents' },
                    { key: 'agency', label: 'Agencies' },
                    { key: 'builder', label: 'Builders' },
                  ].map((r) => (
                    <button
                      key={r.key}
                      className={`filter-chip ${selectedRole === r.key ? 'filter-chip-active' : ''}`}
                      onClick={() => setSelectedRole(r.key)}
                      style={{
                        padding: '7px 16px',
                        borderRadius: '20px',
                        backgroundColor: selectedRole === r.key ? '#6C63FF' : '#F3F4F6',
                        color: selectedRole === r.key ? '#fff' : '#6B7280',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      {r.label}
                    </button>
                  ))}

                  {/* Divider */}
                  <div style={{ width: '1px', height: '24px', backgroundColor: '#E5E7EB', alignSelf: 'center', margin: '0 8px' }}></div>

                  {/* Subscription filters */}
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'subscribed', label: 'Subscribed' },
                    { key: 'free', label: 'Free' },
                    { key: 'unsubscribed', label: 'Unsubscribed' },
                  ].map((f) => (
                    <button
                      key={f.key}
                      className={`filter-chip ${selectedSubFilter === f.key ? 'filter-chip-active-sub' : ''}`}
                      onClick={() => setSelectedSubFilter(f.key)}
                      style={{
                        padding: '7px 16px',
                        borderRadius: '20px',
                        backgroundColor: selectedSubFilter === f.key ? '#059669' : '#F3F4F6',
                        color: selectedSubFilter === f.key ? '#fff' : '#6B7280',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      {f.key === 'all' ? f.label : `${f.label} (${subFilterCounts[f.key] || 0})`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* User Cards */}
            {loading && users.length === 0 ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center p-4">
                <div>No users found</div>
              </div>
            ) : (
              <div className="crm-addpage-cards-list">
                {filteredUsers.map((user) => (
                  <div key={user.id} id={`card-${user.id}`} className="crm-addpage-single-card">
                    {/* Card Header */}
                    <div
                      className="crm-addpage-card-top"
                      onClick={() => toggleCardExpansion(user.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="crm-addpage-company-details">
                        <div className="crm-addpage-name-badge-row">
                          <div
                            className="crm-addpage-company-title"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(user);
                            }}
                          >
                            {user.full_name || user.name || 'Unknown User'}
                          </div>
                          <div className="d-flex gap-2 align-items-center">
                            {/* Role Badge (like mobile app) */}
                            <span
                              style={{
                                backgroundColor: user.role === 'builder' ? '#f59e0b' : user.role === 'agency' ? '#8b5cf6' : '#6366f1',
                                color: 'white',
                                padding: '3px 8px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'capitalize'
                              }}
                            >
                              {user.role || 'User'}
                            </span>

                            {/* Subscription Status Badge (like mobile app) */}
                            <span
                              style={{
                                backgroundColor: getSubscriptionStatus(user) === 'subscribed' ? '#10b981' : '#6b7280',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                textTransform: 'capitalize'
                              }}
                            >
                              {getSubscriptionStatus(user) === 'subscribed' ? 'Subscribed' : 'Free'}
                            </span>
                          </div>
                        </div>
                        <div className="crm-addpage-join-date">
                          Joined: {formatDate(user.created_at)}
                        </div>
                        <div className="crm-addpage-join-date">
                          Email: {user.email || 'N/A'}
                        </div>
                        <div className="crm-addpage-join-date">
                          Phone: {user.phone || 'N/A'}
                        </div>
                      </div>
                      <div className="crm-addpage-actions-row">
                        <button
                          className="crm-addpage-call-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.phone) {
                              window.open(`tel:${user.phone}`, '_self');
                            } else {
                              toast.info('Phone number not available');
                            }
                          }}
                        >
                          Call
                          <img src={callcrm} alt="Call" />
                        </button>
                        <button className="crm-addpage-dropdown-icon">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{
                              transform: expandedId === user.id ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.3s ease",
                            }}
                          >
                            <path
                              d="M19 9L12 16L5 9"
                              stroke="#666"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Expandable Content (EXACT mobile app structure) */}
                    {expandedId === user.id && (
                      <div className="crm-addpage-expand-content">
                        {/* 1. Basic Info Section (like mobile app) */}
                        <div className="info-rows" style={{ marginBottom: '16px' }}>
                          <div className="d-flex justify-content-between mb-2">
                            <div className="info-col">
                              <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>User Source</div>
                              <div style={{ fontSize: '14px', color: '#1F2937', fontWeight: '600' }}>{user.source || 'Direct'}</div>
                            </div>
                            <div className="info-col">
                              <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500' }}>Category</div>
                              <div style={{ fontSize: '14px', color: '#1F2937', fontWeight: '600' }}>{user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}</div>
                            </div>
                          </div>
                        </div>

                        {/* Contact Info (like mobile app) */}
                        <div className="contact-info" style={{ marginBottom: '16px' }}>
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ fontSize: '13px', color: '#6B7280' }}>Email:</span>
                            <span style={{ fontSize: '13px', color: '#1F2937' }}>{user.email || 'N/A'}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ fontSize: '13px', color: '#6B7280' }}>Phone:</span>
                            <span style={{ fontSize: '13px', color: '#1F2937' }}>{user.phone || 'N/A'}</span>
                          </div>
                          {user.city && (
                            <div className="d-flex justify-content-between mb-1">
                              <span style={{ fontSize: '13px', color: '#6B7280' }}>Location:</span>
                              <span style={{ fontSize: '13px', color: '#1F2937' }}>{user.city}{user.state ? `, ${user.state}` : ''}</span>
                            </div>
                          )}
                          {user.rera_id && (
                            <div className="d-flex justify-content-between mb-1">
                              <span style={{ fontSize: '13px', color: '#6B7280' }}>RERA:</span>
                              <span style={{ fontSize: '13px', color: '#1F2937' }}>{user.rera_id}</span>
                            </div>
                          )}
                        </div>

                        {/* 2. Package Info Card (like mobile app) */}
                        {user.subscription?.plan_name && (
                          <div style={{
                            backgroundColor: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '16px'
                          }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{user.subscription.plan_name}</span>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: getSubscriptionStatus(user) === 'subscribed' ? '#D1FAE5' : '#FEE2E2',
                                padding: '2px 8px',
                                borderRadius: '12px'
                              }}>
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: getSubscriptionStatus(user) === 'subscribed' ? '#059669' : '#DC2626',
                                  marginRight: '4px'
                                }}></div>
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  color: getSubscriptionStatus(user) === 'subscribed' ? '#059669' : '#DC2626'
                                }}>
                                  {getSubscriptionStatus(user) === 'subscribed' ? 'Subscribed' : 'Free'}
                                </span>
                              </div>
                            </div>
                            {user.subscription.current_period_start && (
                              <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                                {formatDate(user.subscription.current_period_start)} — {formatDate(user.subscription.current_period_end)}
                              </div>
                            )}
                            {(() => {
                              // Matches mobile app: subscription days + admin-granted free days
                              const { daysRemaining, freeDays, subscriptionDays } = getPackageDaysSplit(user);
                              return daysRemaining > 0 && (
                                <div style={{
                                  fontSize: '12px',
                                  color: daysRemaining <= 7 ? '#DC2626' : '#000000',
                                  fontWeight: '500'
                                }}>
                                  {subscriptionDays} days
                                  {freeDays > 0 && <span style={{ color: '#10B981', fontWeight: '700' }}> +{freeDays} Free</span>}
                                  <span style={{ color: '#10B981' }}> remaining</span>
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* Subscription Package Management */}
                        <div className="crm-border-1">

                          {/* Assign Package (free users) or Extend Package (subscribed users) - EXACT match with mobile app */}
                          {isFreeUser(user) ? (
                            <div style={{ marginBottom: '16px' }}>
                              <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                                Assign Package
                              </h5>
                              {plansLoading ? (
                                <div style={{ textAlign: 'center', padding: '12px', color: '#6366F1' }}>Loading plans...</div>
                              ) : (
                                <>
                                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
                                    Select a plan for this {user.role}
                                  </p>
                                  <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    overflowX: 'auto',
                                    marginBottom: '10px',
                                    paddingBottom: '4px'
                                  }}>
                                    {getRolePlans(user.role).map(plan => (
                                      <button
                                        key={plan.id}
                                        onClick={() => {
                                          setSelectedPlan(plan);
                                          if (!plans.length) loadPlansForRole(user.role);
                                        }}
                                        style={{
                                          minWidth: '120px',
                                          padding: '8px 12px',
                                          border: `2px solid ${selectedPlan?.id === plan.id ? '#6366F1' : '#E5E7EB'}`,
                                          backgroundColor: selectedPlan?.id === plan.id ? '#6366F1' : 'white',
                                          color: selectedPlan?.id === plan.id ? 'white' : '#374151',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <span style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {plan.name}
                                        </span>
                                        <span style={{ fontSize: '11px' }}>
                                          ₹{plan.offer_price}
                                        </span>
                                      </button>
                                    ))}
                                    {getRolePlans(user.role).length === 0 && (
                                      <div style={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
                                        No paid plans available
                                      </div>
                                    )}
                                  </div>
                                  {selectedPlan && (
                                    <div style={{
                                      backgroundColor: '#F3F4F6',
                                      padding: '10px',
                                      borderRadius: '6px',
                                      marginBottom: '12px'
                                    }}>
                                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{selectedPlan.name}</div>
                                      <div style={{ fontSize: '12px', color: '#6B7280' }}>₹{selectedPlan.offer_price} / {selectedPlan.duration_days} days</div>
                                    </div>
                                  )}
                                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>Select No of Days</p>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button
                                      onClick={() => setAssignDays(String(Math.max(1, (parseInt(assignDays) || 0) - 1)))}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: '#6366F1',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      value={assignDays}
                                      onChange={(e) => setAssignDays(e.target.value)}
                                      placeholder="0"
                                      maxLength={3}
                                      style={{
                                        width: '60px',
                                        padding: '6px',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                        fontSize: '14px'
                                      }}
                                    />
                                    <button
                                      onClick={() => setAssignDays(String(Math.min(365, (parseInt(assignDays) || 0) + 1)))}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: '#6366F1',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      +
                                    </button>
                                    <button
                                      onClick={() => handleAssignPlan(user.id)}
                                      disabled={assigning || !selectedPlan}
                                      style={{
                                        backgroundColor: assigning ? '#9CA3AF' : '#6366F1',
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 16px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: assigning ? 'not-allowed' : 'pointer',
                                        marginLeft: '8px'
                                      }}
                                    >
                                      {assigning ? 'Applying...' : 'Apply'}
                                    </button>
                                  </div>
                                  {getAssignCostPreview() !== null && (
                                    <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px', fontWeight: '500' }}>
                                      Cost: ₹{getAssignCostPreview()}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <div style={{ marginBottom: '16px' }}>
                              <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                                Extend Package
                              </h5>
                              <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>Select No of Days</p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                  onClick={() => setExtendDays(String(Math.max(-365, (parseInt(extendDays) || 0) - 1)))}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#6366F1',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: 'white',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  -
                                </button>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={extendDays}
                                  onChange={(e) => setExtendDays(e.target.value.replace(/(?!^-)[^0-9]/g, ''))}
                                  placeholder="0"
                                  maxLength={4}
                                  style={{
                                    width: '60px',
                                    padding: '6px',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontSize: '14px'
                                  }}
                                />
                                <button
                                  onClick={() => setExtendDays(String(Math.min(365, (parseInt(extendDays) || 0) + 1)))}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#6366F1',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: 'white',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  +
                                </button>
                                <button
                                  onClick={() => handleExtend(user.id)}
                                  disabled={extending}
                                  style={{
                                    backgroundColor: extending ? '#9CA3AF' : '#6366F1',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 16px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: extending ? 'not-allowed' : 'pointer',
                                    marginLeft: '8px'
                                  }}
                                >
                                  {extending ? 'Applying...' : 'Apply'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Add Credits - Only show for Builder type */}
                        {user.role?.toLowerCase() === "builder" && (
                          <>
                            <div className="crm-addpage-credits-area">
                              <div className="crm-addpage-credits-heading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Add Credits
                                <span style={{ backgroundColor: '#EDE9FE', borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: 700, color: '#6C63FF' }}>
                                  {creditsBalanceLoading
                                    ? 'Loading...'
                                    : `Available: ${(creditsBalance ?? 0).toLocaleString('en-IN')}`}
                                </span>
                              </div>
                              <div className="crm-addpage-credits-controls">
                                <input
                                  type="number"
                                  className="crm-addpage-credits-field"
                                  placeholder="Enter credits"
                                  value={creditsAmount}
                                  onChange={(e) => setCreditsAmount(e.target.value)}
                                  min="1"
                                />
                                <button
                                  className="crm-addpage-apply-button"
                                  onClick={() => handleAddCredits(user.id)}
                                  disabled={addingCredits}
                                >
                                  {addingCredits ? 'Assigning...' : 'Apply'}
                                </button>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Agent Buttons - Only show for Agent type */}
                        {user.role?.toLowerCase() === "agent" && (
                          <div className="crm-addpage-final-buttons" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              className="crm-addpage-profile-button"
                              onClick={() => navigate('/AdminCrmUserMenu', { state: { userId: user.id, userName: user.full_name || user.name || 'Unknown', userRole: user.role, userEmail: user.email, userPhone: user.phone } })}
                              style={{ flex: 'none', width: '50%' }}
                            >
                              User Profile
                            </button>
                            {/* CRM Access button commented out
                            <button
                              className="crm-addpage-access-button"
                              onClick={() => handleCrmAccessClick(user)}
                            >
                              CRM Access
                            </button>
                            */}
                          </div>
                        )}

                        {/* Manage Sub-Users - Only show for Agency and Builder types (EXACT match with mobile app) */}
                        {(user.role?.toLowerCase() === "agency" || user.role?.toLowerCase() === "builder") && (
                          <>
                            <div style={{ borderTop: '1px solid #e0e0e0', margin: '15px 0' }}></div>
                            <div className="crm-addpage-manage-section">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                                <div className="crm-addpage-manage-heading">Manage Users</div>
                                <button
                                  onClick={() => fetchSubUsers(user.id)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#6366F1',
                                    cursor: 'pointer',
                                    fontSize: '18px'
                                  }}
                                  disabled={loadingSubUsers}
                                >
                                  🔄
                                </button>
                              </div>

                              {/* Sub-user role tabs (EXACT match with mobile app) */}
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                                {SUB_USER_ROLES.map(role => {
                                  const used = subUserLimits?.used_sub_users?.[role.key] ?? 0;
                                  const max = subUserLimits?.max_sub_users?.[role.key] ?? 0;
                                  return (
                                    <button
                                      key={role.key}
                                      onClick={() => {
                                        setActiveSubTab(role.key);
                                        fetchSubUsers(user.id);
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        border: `1px solid ${activeSubTab === role.key ? '#6366F1' : '#e0e0e0'}`,
                                        backgroundColor: activeSubTab === role.key ? '#6366F1' : 'white',
                                        color: activeSubTab === role.key ? 'white' : '#666',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {role.label}s {subUserLimits ? `(${used}/${max})` : ''}
                                    </button>
                                  );
                                })}
                              </div>

                              {loadingSubUsers ? (
                                <div className="text-center py-5">
                                  <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
                                </div>
                              ) : (
                                <>
                                  {/* Filtered sub-users list */}
                                  {subUsers.filter(su => su.role === activeSubTab).length === 0 && (
                                    <div className="crm-addpage-no-users-warning">No users remaining</div>
                                  )}

                                  {subUsers.filter(su => su.role === activeSubTab).map(subUser => {
                                    const suName = subUser.full_name || subUser.email || 'User';
                                    const suInitials = suName.substring(0, 2).toUpperCase();

                                    return (
                                      <div key={subUser.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '10px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        marginBottom: '8px'
                                      }}>
                                        <div style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '18px',
                                          backgroundColor: '#6366F1',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: '12px',
                                          fontWeight: 'bold',
                                          marginRight: '12px'
                                        }}>
                                          {suInitials}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{suName}</div>
                                          <div style={{ fontSize: '12px', color: '#666' }}>
                                            {subUser.phone} {subUser.city ? `• ${subUser.city}` : ''}
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => {
                                            setEditingSubUser(subUser);
                                            setSubUserForm({
                                              full_name: subUser.full_name || '',
                                              email: subUser.email || '',
                                              phone: subUser.phone || '',
                                              city: subUser.city || '',
                                              role: activeSubTab,
                                              password: ''
                                            });
                                            setShowAddSubUser(true);
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#6366F1',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            marginRight: '8px'
                                          }}
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          onClick={() => handleDeleteSubUser(user.id, subUser.id, subUser.full_name || 'this user')}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#EF4444',
                                            cursor: 'pointer',
                                            fontSize: '16px'
                                          }}
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    );
                                  })}

                                  {/* Remaining slots indicator (EXACT match with mobile app) */}
                                  {(() => {
                                    const remaining = subUserLimits?.remaining?.[activeSubTab] ?? null;
                                    const roleLabel = SUB_USER_ROLES.find(r => r.key === activeSubTab)?.label || 'User';
                                    const canAdd = remaining === null || remaining > 0;
                                    return (
                                      <>
                                        {subUserLimits && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '14px' }}>{remaining > 0 ? '✅' : '⚠️'}</span>
                                            <span style={{ fontSize: '11px', fontWeight: '600', color: remaining > 0 ? '#059669' : '#DC2626' }}>
                                              {remaining > 0 ? `${remaining} slot(s) remaining` : `No ${roleLabel.toLowerCase()} slots remaining`}
                                            </span>
                                          </div>
                                        )}
                                        {/* Add sub-user button */}
                                        <button
                                          onClick={() => {
                                            if (!canAdd) {
                                              toast.warning(`No ${roleLabel} Slots: This user has used all ${roleLabel.toLowerCase()} slots. They need to purchase an addon to add more.`);
                                              return;
                                            }
                                            setEditingSubUser(null);
                                            setSubUserForm({
                                              full_name: '',
                                              email: '',
                                              phone: '',
                                              city: '',
                                              role: activeSubTab,
                                              password: ''
                                            });
                                            setShowAddSubUser(true);
                                          }}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '10px',
                                            border: `1px dashed ${canAdd ? '#6366F1' : '#9CA3AF'}`,
                                            borderRadius: '8px',
                                            backgroundColor: 'transparent',
                                            color: canAdd ? '#6366F1' : '#9CA3AF',
                                            cursor: canAdd ? 'pointer' : 'not-allowed',
                                            width: '100%',
                                            justifyContent: 'center',
                                            opacity: canAdd ? 1 : 0.4
                                          }}
                                        >
                                          <span>+</span>
                                          <span>Add {SUB_USER_ROLES.find(r => r.key === activeSubTab)?.label}</span>
                                        </button>
                                      </>
                                    );
                                  })()}
                                </>
                              )}

                              {/* Action buttons */}
                              <div className="crm-addpage-final-buttons" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                  className="crm-addpage-profile-button"
                                  onClick={() => navigate('/AdminCrmUserMenu', { state: { userId: user.id, userName: user.full_name || user.name || 'Unknown', userRole: user.role, userEmail: user.email, userPhone: user.phone } })}
                                  style={{ flex: 'none', width: '50%' }}
                                >
                                  User Profile
                                </button>
                                {/* CRM Access button commented out
                                <button
                                  className="crm-addpage-access-button"
                                  onClick={() => handleCrmAccessClick(user)}
                                >
                                  CRM Access
                                </button>
                                */}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Load More Button */}
            {hasMore && !loading && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => loadMore()}
                  disabled={loading}
                >
                  Load More Users
                </button>
              </div>
            )}

            {/* Loading indicator */}
            {loading && users.length > 0 && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            )}

            {/* Add User Button - hidden, matching mobile app (floating Add User button commented out there too) */}
            {/* <button className="crm-addpage-floating-add" onClick={() => setShowAddUserModal(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="white" />
                <path d="M12 14.5C6.99 14.5 2.91 17.86 2.91 22C2.91 22.28 3.13 22.5 3.41 22.5H20.59C20.87 22.5 21.09 22.28 21.09 22C21.09 17.86 17.01 14.5 12 14.5Z" fill="white" />
              </svg>
              Add User
            </button> */}

            {/* Date Range Filter Modal (join date) - matching mobile app */}
            {showDateFilterModal && (
              <div className="modal-overlay" onClick={() => setShowDateFilterModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Filter by Join Date</h3>
                    <button style={{ border: "none", backgroundColor: "#fff" }} onClick={() => setShowDateFilterModal(false)}>
                      <img src={close} alt="Close" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {/* Shows DD-MM-YYYY; the transparent native date input on top opens the calendar */}
                    {[
                      { label: 'Start Date', value: startDate, set: setStartDate, min: undefined, max: endDate || undefined },
                      { label: 'End Date', value: endDate, set: setEndDate, min: startDate || undefined, max: new Date().toLocaleDateString('en-CA') },
                    ].map((d) => (
                      <div className="form-group" key={d.label}>
                        <label className="form-label">{d.label}</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="DD-MM-YYYY"
                            value={toDisplayDate(d.value)}
                            readOnly
                          />
                          <input
                            type="date"
                            value={d.value}
                            min={d.min}
                            max={d.max}
                            onChange={(e) => d.set(e.target.value)}
                            onClick={(e) => e.currentTarget.showPicker?.()}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button className="btn-secondary" onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
                    <button className="btn-primary" onClick={() => setShowDateFilterModal(false)}>Done</button>
                  </div>
                </div>
              </div>
            )}

            {/* Package Assignment Modal */}
            {showPackageModal && (
              <div className="modal-overlay" onClick={() => setShowPackageModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Assign/Extend Package</h3>
                    <button style={{ border: "none", backgroundColor: "#fff" }} onClick={() => setShowPackageModal(false)}>
                      <img src={close} alt="Close" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {selectedUser && (
                      <div className="mb-3">
                        <strong>User:</strong> {selectedUser.full_name || selectedUser.name}
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Select Package</label>
                      <select
                        className="form-control"
                        value={selectedPackageId}
                        onChange={(e) => setSelectedPackageId(e.target.value)}
                      >
                        <option value="">Select a package</option>
                        {packageData.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} - ₹{pkg.offer_price}/{pkg.duration_days} days
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Duration (Months)</label>
                      <div className="d-flex align-items-center gap-3">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setExtendMonths(Math.max(1, extendMonths - 1))}
                        >
                          -
                        </button>
                        <span className="fw-bold">{extendMonths}</span>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setExtendMonths(extendMonths + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn-secondary" onClick={() => setShowPackageModal(false)}>Cancel</button>
                    <button
                      className="btn-primary"
                      onClick={handleAssignPackage}
                      disabled={packageLoading || !selectedPackageId}
                    >
                      {packageLoading ? 'Assigning...' : 'Assign Package'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sub-User Modal */}
            {showSubUserModal && (
              <div className="modal-overlay" onClick={() => setShowSubUserModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Add Sub-User</h3>
                    <button style={{ border: "none", backgroundColor: "#fff" }} onClick={() => setShowSubUserModal(false)}>
                      <img src={close} alt="Close" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {selectedUser && (
                      <div className="mb-3">
                        <strong>Parent User:</strong> {selectedUser.full_name || selectedUser.name} ({selectedUser.role})
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-control">
                        <option value="">Select role</option>
                        <option value="manager">Manager</option>
                        <option value="assistant">Assistant</option>
                        <option value="sales">Sales Representative</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn-secondary" onClick={() => setShowSubUserModal(false)}>Cancel</button>
                    <button className="btn-primary" disabled={subUserLoading}>
                      {subUserLoading ? 'Adding...' : 'Add Sub-User'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Modal */}
            {confirmOpen && (
              <div className="mu-modal-overlay" onClick={() => setConfirmOpen(false)}>
                <div className="mu-confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="mu-confirm-icon">!</div>
                  <h4 className="mu-confirm-title">Are you sure?</h4>
                  <p className="mu-confirm-body">Are you sure you want to delete this user? This action cannot be undone.</p>
                  <div className="mu-confirm-actions">
                    <button className="btn btn-light mu-confirm-cancel" onClick={() => setConfirmOpen(false)}>Cancel</button>
                    <button className="btn btn-danger mu-confirm-delete" onClick={handleDeleteConfirmed}>Delete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Add User Modal (EXACT match with mobile app AdminCrmUsers.js) */}
            {showAddUserModal && (
              <div className="modal-overlay" onClick={() => resetAddUserForm()}>
                <div className="add-user-sheet" onClick={(e) => e.stopPropagation()}>
                  <div className="add-user-header">
                    <h3 className="add-user-title">Register New User</h3>
                    <button
                      style={{ border: "none", backgroundColor: "transparent", cursor: "pointer" }}
                      onClick={() => resetAddUserForm()}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}>

                    {/* Full Name */}
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control mobile-style-input"
                        placeholder="Full Name *"
                        value={addUserForm.first_name + (addUserForm.last_name ? ' ' + addUserForm.last_name : '')}
                        onChange={(e) => {
                          const names = e.target.value.split(' ');
                          setAddUserForm(p => ({
                            ...p,
                            first_name: names[0] || '',
                            last_name: names.slice(1).join(' ') || ''
                          }));
                        }}
                      />
                    </div>

                    {/* Email */}
                    <div className="form-group">
                      <input
                        type="email"
                        className="form-control mobile-style-input"
                        placeholder="Email *"
                        value={addUserForm.email}
                        onChange={(e) => setAddUserForm(p => ({ ...p, email: e.target.value }))}
                      />
                    </div>

                    {/* Phone */}
                    <div className="form-group">
                      <input
                        type="tel"
                        className="form-control mobile-style-input"
                        placeholder="Phone *"
                        value={addUserForm.phone}
                        onChange={(e) => setAddUserForm(p => ({ ...p, phone: e.target.value }))}
                      />
                    </div>

                    {/* Role Selection */}
                    <div className="form-group">
                      <div className="mobile-role-picker-row">
                        {[{ key: 'agent', label: 'Agent' }, { key: 'agency', label: 'Agency' }, { key: 'builder', label: 'Builder' }].map(r => (
                          <button
                            key={r.key}
                            type="button"
                            className={`mobile-role-picker-btn ${addUserForm.role === r.key ? 'mobile-role-picker-active' : ''}`}
                            onClick={() => {
                              setAddUserForm(p => ({ ...p, role: r.key }));
                            }}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {addUserForm.role && (
                      <>
                        {/* Company Name for Agency/Builder */}
                        {(addUserForm.role === 'agency' || addUserForm.role === 'builder') && (
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-control mobile-style-input"
                              placeholder="Company Name"
                              value={addUserForm.company_name}
                              onChange={(e) => setAddUserForm(p => ({ ...p, company_name: e.target.value }))}
                            />
                          </div>
                        )}

                        {/* City */}
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-control mobile-style-input"
                            placeholder="City"
                            value={addUserForm.city}
                            onChange={(e) => setAddUserForm(p => ({ ...p, city: e.target.value }))}
                          />
                        </div>

                        {/* State */}
                        <div className="form-group">
                          <input
                            type="text"
                            className="form-control mobile-style-input"
                            placeholder="State"
                            value={addUserForm.state}
                            onChange={(e) => setAddUserForm(p => ({ ...p, state: e.target.value }))}
                          />
                        </div>

                        {/* Password */}
                        <div className="form-group">
                          <input
                            type="password"
                            className="form-control mobile-style-input"
                            placeholder="Password (default: Rebax@123)"
                            value={addUserForm.password}
                            onChange={(e) => setAddUserForm(p => ({ ...p, password: e.target.value }))}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Register User Button */}
                  <div style={{ padding: '24px' }}>
                    <button
                      type="button"
                      className="mobile-register-btn"
                      onClick={handleSimpleRegisterUser}
                      disabled={addingUser}
                    >
                      {addingUser ? 'Registering...' : 'Register User'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Centered Alert Modal */}
      {showAlert && (
        <div className="alert-overlay">
          <div className="alert-modal">
            <div className="alert-content">
              <p className="alert-message">{alertMessage}</p>
              <button className="alert-ok-btn" onClick={hideAlert}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Picker Modal */}
      {isDatePickerVisible &&
        createPortal(
          <>
            {/* Full-page transparent dark background */}
            <div
              className="crm-datepicker-fullscreen"
              onClick={() => setIsDatePickerVisible(false)}
            ></div>

            {/* Date picker */}
            <div
              className="crm-datepicker-overlay"
              style={{
                top: pickerPosition.top + 10,
                left: pickerPosition.left,
                width: pickerPosition.width,
              }}
            >
              <div className="crm-datepicker-box">
                <div className="crm-selection-line"></div>

                {/* Day */}
                <div
                  className="crm-datepicker-col"
                  ref={dayRef}
                  onScroll={() =>
                    handleScroll(dayRef, setSelectedDayIdx, days.length)
                  }
                >
                  <div className="crm-datepicker-item crm-spacer"></div>
                  {days.map((d, i) => (
                    <div
                      key={i}
                      className={`crm-datepicker-item ${i === selectedDayIdx ? "crm-selected" : ""
                        }`}
                    >
                      {d}
                    </div>
                  ))}
                  <div className="crm-datepicker-item crm-spacer"></div>
                </div>

                {/* Month */}
                <div
                  className="crm-datepicker-col"
                  ref={monthRef}
                  onScroll={() =>
                    handleScroll(monthRef, setSelectedMonthIdx, months.length)
                  }
                >
                  <div className="crm-datepicker-item crm-spacer"></div>
                  {months.map((m, i) => (
                    <div
                      key={i}
                      className={`crm-datepicker-item ${i === selectedMonthIdx ? "crm-selected" : ""
                        }`}
                    >
                      {m}
                    </div>
                  ))}
                  <div className="crm-datepicker-item crm-spacer"></div>
                </div>

                {/* Year */}
                <div
                  className="crm-datepicker-col"
                  ref={yearRef}
                  onScroll={() =>
                    handleScroll(yearRef, setSelectedYearIdx, years.length)
                  }
                >
                  <div className="crm-datepicker-item crm-spacer"></div>
                  {years.map((y, i) => (
                    <div
                      key={i}
                      className={`crm-datepicker-item ${i === selectedYearIdx ? "crm-selected" : ""
                        }`}
                    >
                      {y}
                    </div>
                  ))}
                  <div className="crm-datepicker-item crm-spacer"></div>
                </div>

                <div className="crm-datepicker-actions">
                  <button
                    className="crm-cancel-btn"
                    onClick={() => setIsDatePickerVisible(false)}
                  >
                    Cancel
                  </button>
                  <button className="crm-save-btn" onClick={handleSaveDate}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Enter Your Password</h3>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handlePasswordSubmit();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPasswordModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handlePasswordSubmit}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="modal-overlay" onClick={() => setShowOtpModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Enter OTP</h3>
              <button className="modal-close" onClick={() => setShowOtpModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" style={{ marginBottom: "20px" }}>OTP (4 digits)</label>
                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-input-${index}`}
                      type="text"
                      className="form-control"
                      value={digit}
                      maxLength="1"
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleOtpChange(index, value);
                      }}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      style={{
                        width: "50px",
                        height: "50px",
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: "bold"
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowOtpModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleOtpSubmit}>Verify</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={handleSuccessClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
            <div className="modal-header" style={{ borderBottom: "none", paddingBottom: "0" }}>
              <button className="modal-close" onClick={handleSuccessClose}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: "center", padding: "30px 20px" }}>
              <div style={{ fontSize: "60px", color: "#28a745", marginBottom: "20px" }}>✓</div>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>CRM Access Granted!</h3>
              <p style={{ color: "#666", fontSize: "14px" }}>You now have access to the CRM system</p>
            </div>
            <div className="modal-footer" style={{ borderTop: "none", justifyContent: "center" }}>
              <button className="btn-primary" onClick={handleSuccessClose} style={{ minWidth: "120px" }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-User Add/Edit Modal (EXACT match with mobile app) */}
      {showAddSubUser && (
        <div className="modal-overlay" onClick={() => setShowAddSubUser(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingSubUser ? 'Edit' : 'Add'} {SUB_USER_ROLES.find(r => r.key === activeSubTab)?.label}
              </h3>
              <button className="modal-close" onClick={() => setShowAddSubUser(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Full Name"
                  value={subUserForm.full_name}
                  onChange={(e) => setSubUserForm(p => ({ ...p, full_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={subUserForm.email}
                  onChange={(e) => setSubUserForm(p => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="Phone"
                  value={subUserForm.phone}
                  onChange={(e) => setSubUserForm(p => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="City"
                  value={subUserForm.city}
                  onChange={(e) => setSubUserForm(p => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password (default: Rebax@123)</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Password"
                  value={subUserForm.password}
                  onChange={(e) => setSubUserForm(p => ({ ...p, password: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddSubUser(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleAddSubUser(expandedId)}
              >
                {editingSubUser ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}